/**
 * probeHostCapability — per-host, empirical answer to "can this task pane get a
 * live microphone?".
 *
 * Realtime voice needs a real mic stream and a WebRTC peer connection inside the
 * task-pane webview. Whether that is possible is NOT answerable from the type
 * definitions or the docs — the three hosts we ship to (classic Outlook desktop,
 * new Outlook on Windows, Outlook on the web) each embed a different webview
 * with different permission plumbing. This module runs the capability probes;
 * `components/HostCapabilityDiagnostics.tsx` renders them and produces a
 * plain-text report the operator pastes back.
 *
 * What the shipped @types/office-js (1.0.592) actually declares — read before
 * trusting any second-hand account of this API:
 *
 *  - `index.d.ts:251` — `const devicePermission: DevicePermission;` inside
 *    `declare namespace Office`. The path is **`Office.devicePermission`**.
 *    `Office.context.devicePermission` does NOT exist in the type definitions:
 *    "devicePermission" occurs exactly ONCE in the whole file, at line 251, and
 *    `interface Context` (line 4639) has no such member.
 *  - `index.d.ts:5512` — `interface DevicePermission` has TWO methods:
 *    `requestPermissions(permissions: Office.DevicePermissionType[]): Promise<boolean>`
 *    and `requestPermissionsAsync(permissions: Office.DevicePermissionType[], [options,] callback)`.
 *  - The argument is the `Office.DevicePermissionType` ENUM (`camera` /
 *    `geolocation` / `microphone`, line 536) — NOT an array of `{type: "..."}`
 *    object literals.
 *  - Gating (line 5510): requirement set **DevicePermission 1.1**, a COMMON set,
 *    not a Mailbox set — so it cannot be declared in the manifest's
 *    `<Requirements>` block and bumping Mailbox 1.5 → 1.8 buys nothing here.
 *  - Line 5532 (on `requestPermissions`): "This method isn't supported in
 *    Outlook add-ins. Use the `requestPermissionsAsync` method instead."
 *  - Lines 5567-5569 (on `requestPermissionsAsync`): verify
 *    `Office.context.mailbox.diagnostics.hostName` "returns `OutlookWebApp`.
 *    Otherwise, the `requestPermissionsAsync` call will return an error."
 *    Applications list (5496-5508): the web hosts, "also supported in new
 *    Outlook on Windows". Classic Outlook desktop is not in that list.
 *
 * So the types say web-only. The probes below deliberately test BOTH the
 * declared `Office.devicePermission` path AND the undeclared
 * `Office.context.devicePermission` path, and report which (if either) actually
 * exists on each host — because a real host, not a .d.ts, is the authority on
 * what is present at runtime.
 *
 * Design rules:
 *  - Nothing here throws. Every probe is guarded and degrades to a row.
 *  - The two probes that raise a permission prompt (Office's modal and
 *    `getUserMedia`) are NOT run on mount — they are operator-triggered, so the
 *    operator controls ordering (the Office grant is documented to need a frame
 *    reload before the browser layer sees it).
 *  - `getUserMedia` stops every track the instant it resolves. The mic is never
 *    held open.
 *  - No Realtime session is attempted. Capability probing only.
 */

/** Three-value verdict. `unavailable` = the probe could not run at all. */
export type ProbeStatus = "pass" | "fail" | "unavailable";

export interface ProbeRow {
  /** Stable key — the action probes return a row with the same id they replace. */
  id: string;
  label: string;
  status: ProbeStatus;
  /** One line: the value read, or the raw error when one occurred. */
  summary: string;
  /** Ordered key/value lines printed under the row in the text report. */
  details?: Array<[string, string]>;
}

export interface HostCapabilityReport {
  generatedAt: string;
  href: string | null;
  userAgent: string | null;
  rows: ProbeRow[];
}

/** Row ids the operator-triggered probes replace. */
export const OFFICE_PERMISSION_ROW_ID = "office-device-permission-request";
export const GET_USER_MEDIA_ROW_ID = "get-user-media";

/**
 * The requirement sets that decide this question.
 *
 * Mailbox 1.5 is the manifest root `<Requirements>` floor; Mailbox 1.8 is the
 * `VersionOverridesV1_1` floor (it carries `<SupportsSharedFolders>`). The rest
 * of the Mailbox ladder is free and pins down exactly how modern each host is.
 * DevicePermission 1.1 is the set `Office.devicePermission` is gated on
 * (@types/office-js index.d.ts:5510) — the one that actually matters here.
 */
export const PROBED_REQUIREMENT_SETS: ReadonlyArray<readonly [string, string]> = [
  ["Mailbox", "1.1"],
  ["Mailbox", "1.5"],
  ["Mailbox", "1.8"],
  ["Mailbox", "1.9"],
  ["Mailbox", "1.10"],
  ["Mailbox", "1.13"],
  ["Mailbox", "1.15"],
  ["DevicePermission", "1.1"],
];

/* -------------------------------------------------------------------------- */
/* Structural views of the host globals                                       */
/* -------------------------------------------------------------------------- */

interface AsyncResultLike {
  status?: unknown;
  value?: unknown;
  error?: { code?: unknown; name?: unknown; message?: unknown };
}

interface DevicePermissionLike {
  requestPermissions?: (permissions: unknown[]) => Promise<boolean>;
  requestPermissionsAsync?: (
    permissions: unknown[],
    callback: (asyncResult: AsyncResultLike) => void,
  ) => void;
}

interface OfficeLike {
  context?: {
    platform?: unknown;
    contentLanguage?: unknown;
    displayLanguage?: unknown;
    diagnostics?: { host?: unknown; platform?: unknown; version?: unknown };
    requirements?: { isSetSupported?: (name: string, minVersion?: string) => boolean };
    mailbox?: { diagnostics?: { hostName?: unknown; hostVersion?: unknown; OWAView?: unknown } };
    /**
     * NOT declared by @types/office-js — probed on purpose. Second-hand research
     * claims this path works on classic Outlook desktop; the .d.ts says the API
     * lives at `Office.devicePermission`. Only a real host settles it.
     */
    devicePermission?: DevicePermissionLike;
  };
  /** The path the .d.ts actually declares (index.d.ts:251). */
  devicePermission?: DevicePermissionLike;
  DevicePermissionType?: Record<string, unknown>;
  AsyncResultStatus?: { Succeeded?: unknown; Failed?: unknown };
}

/**
 * Read the Office global off `globalThis` rather than the ambient `Office`
 * binding: outside a host (jsdom, a plain browser tab) the identifier is not
 * declared at runtime and a bare `Office?.context` still throws ReferenceError.
 */
function officeGlobal(): OfficeLike | undefined {
  return (globalThis as { Office?: OfficeLike }).Office;
}

/* -------------------------------------------------------------------------- */
/* Small helpers                                                              */
/* -------------------------------------------------------------------------- */

/** `name: message`, or whatever of the two exists. Never throws. */
export function describeError(err: unknown): string {
  if (err && typeof err === "object") {
    const e = err as { name?: unknown; message?: unknown; code?: unknown };
    const name = typeof e.name === "string" && e.name ? e.name : "";
    const message = typeof e.message === "string" && e.message ? e.message : "";
    const code =
      e.code === undefined || e.code === null || e.code === "" ? "" : `code ${String(e.code)}`;
    const head = name || code;
    if (head && message) return `${head}: ${message}`;
    if (head) return head;
    if (message) return message;
  }
  try {
    return String(err);
  } catch {
    return "<unstringifiable error>";
  }
}

/**
 * Read a property that a host object may implement as a throwing getter.
 * Exported so features/composeSurface/probeComposeItem.ts reuses it rather than
 * forking a second copy (ui/CLAUDE.md §1).
 */
export function safeRead(read: () => unknown): string {
  try {
    const v = read();
    if (v === undefined) return "<undefined>";
    if (v === null) return "<null>";
    return String(v);
  } catch (err) {
    return `<threw ${describeError(err)}>`;
  }
}

/**
 * Every property name reachable on `obj` (own + prototype chain), split into
 * the ones that are callable and the rest. Host objects routinely put their
 * methods on a prototype, so `Object.keys` alone under-reports the shape.
 */
export function describeShape(obj: object): { methods: string[]; other: string[] } {
  const methods = new Set<string>();
  const other = new Set<string>();
  let cursor: object | null = obj;
  let depth = 0;
  while (cursor && cursor !== Object.prototype && depth < 10) {
    let names: string[] = [];
    try {
      names = Object.getOwnPropertyNames(cursor);
    } catch {
      names = [];
    }
    for (const key of names) {
      if (key === "constructor") continue;
      let isFn = false;
      try {
        isFn = typeof (obj as Record<string, unknown>)[key] === "function";
      } catch {
        // A getter threw — record the name, classify as non-method.
      }
      (isFn ? methods : other).add(key);
    }
    try {
      cursor = Object.getPrototypeOf(cursor) as object | null;
    } catch {
      cursor = null;
    }
    depth += 1;
  }
  return { methods: [...methods].sort(), other: [...other].sort() };
}

/* -------------------------------------------------------------------------- */
/* Probe 1 — host and platform identity                                       */
/* -------------------------------------------------------------------------- */

/** Exported so the compose diagnostics panel reports the same host block. */
export function probeHostIdentity(): ProbeRow {
  const office = officeGlobal();
  if (!office) {
    return {
      id: "host-identity",
      label: "Host and platform identity",
      status: "unavailable",
      summary: "no Office global — not running inside an Office host",
    };
  }

  const mailboxDiag = (() => {
    try {
      return office.context?.mailbox?.diagnostics;
    } catch {
      return undefined;
    }
  })();
  // Office.context.diagnostics is Mailbox 1.5+ in Outlook and can throw below it.
  const commonDiag = (() => {
    try {
      return office.context?.diagnostics;
    } catch {
      return undefined;
    }
  })();

  const hostName = mailboxDiag ? safeRead(() => mailboxDiag.hostName) : "<unavailable>";
  const hostVersion = mailboxDiag ? safeRead(() => mailboxDiag.hostVersion) : "<unavailable>";

  const details: Array<[string, string]> = [
    ["Office.context.mailbox.diagnostics.hostName", hostName],
    ["Office.context.mailbox.diagnostics.hostVersion", hostVersion],
    [
      "Office.context.mailbox.diagnostics.OWAView",
      mailboxDiag ? safeRead(() => mailboxDiag.OWAView) : "<unavailable>",
    ],
    ["Office.context.platform", safeRead(() => office.context?.platform)],
    [
      "Office.context.diagnostics.host",
      commonDiag ? safeRead(() => commonDiag.host) : "<unavailable>",
    ],
    [
      "Office.context.diagnostics.platform",
      commonDiag ? safeRead(() => commonDiag.platform) : "<unavailable>",
    ],
    [
      "Office.context.diagnostics.version",
      commonDiag ? safeRead(() => commonDiag.version) : "<unavailable>",
    ],
    ["Office.context.displayLanguage", safeRead(() => office.context?.displayLanguage)],
    ["Office.context.contentLanguage", safeRead(() => office.context?.contentLanguage)],
  ];

  const readable = hostName !== "<unavailable>" && !hostName.startsWith("<");
  return {
    id: "host-identity",
    label: "Host and platform identity",
    status: readable ? "pass" : "fail",
    summary: readable
      ? `${hostName} ${hostVersion} on ${safeRead(() => office.context?.platform)}`
      : "Office global present but the host identity could not be read",
    details,
  };
}

/* -------------------------------------------------------------------------- */
/* Probe 2 — requirement sets                                                 */
/* -------------------------------------------------------------------------- */

/**
 * Exported (with the probed list as a parameter) so the compose diagnostics
 * panel asks the same question with its own set list instead of forking this.
 */
export function probeRequirementSets(
  sets: ReadonlyArray<readonly [string, string]> = PROBED_REQUIREMENT_SETS,
): ProbeRow[] {
  const office = officeGlobal();
  const isSetSupported = (() => {
    try {
      return office?.context?.requirements?.isSetSupported;
    } catch {
      return undefined;
    }
  })();

  return sets.map(([name, version]) => {
    const id = `requirement:${name}@${version}`;
    const label = `Requirement set ${name} ${version}`;
    if (typeof isSetSupported !== "function") {
      return {
        id,
        label,
        status: "unavailable" as const,
        summary: "Office.context.requirements.isSetSupported is not available",
      };
    }
    try {
      const supported = isSetSupported(name, version) === true;
      return {
        id,
        label,
        status: supported ? ("pass" as const) : ("fail" as const),
        summary: supported ? "supported" : "not supported",
      };
    } catch (err) {
      return {
        id,
        label,
        status: "unavailable" as const,
        summary: `isSetSupported threw — ${describeError(err)}`,
      };
    }
  });
}

/* -------------------------------------------------------------------------- */
/* Probe 3 — does a devicePermission object exist, and what shape?            */
/* -------------------------------------------------------------------------- */

function describePermissionObject(obj: unknown): string {
  if (obj === undefined) return "absent";
  if (obj === null) return "present but null";
  if (typeof obj !== "object" && typeof obj !== "function") {
    return `present but ${typeof obj}`;
  }
  const { methods, other } = describeShape(obj as object);
  const parts = [`methods: ${methods.length ? methods.join(", ") : "<none>"}`];
  if (other.length) parts.push(`other keys: ${other.join(", ")}`);
  return `present — ${parts.join(" | ")}`;
}

function probeDevicePermissionApi(): ProbeRow {
  const office = officeGlobal();
  if (!office) {
    return {
      id: "device-permission-api",
      label: "Office device-permission API",
      status: "unavailable",
      summary: "no Office global — not running inside an Office host",
    };
  }

  const declaredPath = (() => {
    try {
      return office.devicePermission;
    } catch {
      return undefined;
    }
  })();
  // The undeclared path, probed on purpose — see the module header.
  const contextPath = (() => {
    try {
      return office.context?.devicePermission;
    } catch {
      return undefined;
    }
  })();

  const permTypes = (() => {
    try {
      const t = office.DevicePermissionType;
      if (!t) return "absent";
      return `present — ${Object.keys(t).join(", ") || "<no enumerable keys>"}`;
    } catch (err) {
      return `<threw ${describeError(err)}>`;
    }
  })();

  const details: Array<[string, string]> = [
    ["Office.devicePermission (the path @types/office-js declares)", describePermissionObject(declaredPath)],
    ["Office.context.devicePermission (NOT in @types/office-js)", describePermissionObject(contextPath)],
    ["Office.DevicePermissionType", permTypes],
  ];

  const target = declaredPath ?? contextPath;
  if (!target) {
    return {
      id: "device-permission-api",
      label: "Office device-permission API",
      status: "unavailable",
      summary: "neither Office.devicePermission nor Office.context.devicePermission exists here",
      details,
    };
  }

  const where = declaredPath ? "Office.devicePermission" : "Office.context.devicePermission";
  const { methods } = describeShape(target as object);
  const usable =
    typeof target.requestPermissionsAsync === "function" ||
    typeof target.requestPermissions === "function";
  return {
    id: "device-permission-api",
    label: "Office device-permission API",
    status: usable ? "pass" : "fail",
    summary: usable
      ? `${where} exists (${methods.join(", ") || "no callable members"})`
      : `${where} exists but exposes no request method (${methods.join(", ") || "no callable members"})`,
    details,
  };
}

/* -------------------------------------------------------------------------- */
/* Probe 4 — request microphone permission through Office (operator-triggered) */
/* -------------------------------------------------------------------------- */

function pendingOfficePermissionRow(): ProbeRow {
  return {
    id: OFFICE_PERMISSION_ROW_ID,
    label: "Office microphone permission request",
    status: "unavailable",
    summary: 'not run yet — tap "Request mic via Office"',
  };
}

/** How long to wait for a host callback before declaring it lost. */
const OFFICE_CALLBACK_TIMEOUT_MS = 180_000;

/**
 * Ask Office for the microphone. Prefers `requestPermissionsAsync` (the .d.ts
 * says the promise-returning `requestPermissions` "isn't supported in Outlook
 * add-ins") and falls back to the promise flavour only when the async one is
 * missing, recording which one was actually called.
 *
 * Per the .d.ts, `asyncResult.value === true` means the permission was granted
 * for the FIRST time and the add-in must reload before the webview honours it;
 * `false` means it was already granted and no reload is needed.
 */
export async function requestOfficeMicrophonePermission(): Promise<ProbeRow> {
  const base = { id: OFFICE_PERMISSION_ROW_ID, label: "Office microphone permission request" };
  const office = officeGlobal();
  const target = (() => {
    try {
      return office?.devicePermission ?? office?.context?.devicePermission;
    } catch {
      return undefined;
    }
  })();

  if (!target) {
    return {
      ...base,
      status: "unavailable",
      summary: "no device-permission object on this host — nothing to call",
    };
  }

  // Send the enum member the .d.ts specifies; fall back to the bare string only
  // when the host does not expose the enum. Record what was actually sent.
  const enumValue = (() => {
    try {
      const t = office?.DevicePermissionType;
      if (t && "microphone" in t) return t.microphone;
    } catch {
      /* fall through */
    }
    return "microphone";
  })();
  const sent: Array<[string, string]> = [
    ["permission argument sent", `[${String(enumValue)}] (Office.DevicePermissionType.microphone)`],
  ];

  if (typeof target.requestPermissionsAsync === "function") {
    return new Promise<ProbeRow>((resolve) => {
      let settled = false;
      const finish = (row: ProbeRow) => {
        if (settled) return;
        settled = true;
        resolve(row);
      };
      const timer = setTimeout(
        () =>
          finish({
            ...base,
            status: "fail",
            summary: `requestPermissionsAsync callback did not fire within ${
              OFFICE_CALLBACK_TIMEOUT_MS / 1000
            }s`,
            details: [...sent, ["method called", "requestPermissionsAsync"]],
          }),
        OFFICE_CALLBACK_TIMEOUT_MS,
      );
      try {
        target.requestPermissionsAsync!([enumValue], (asyncResult: AsyncResultLike) => {
          clearTimeout(timer);
          const succeededToken = (() => {
            try {
              return office?.AsyncResultStatus?.Succeeded;
            } catch {
              return undefined;
            }
          })();
          const hasError = asyncResult?.error !== undefined && asyncResult?.error !== null;
          const succeeded =
            succeededToken !== undefined ? asyncResult?.status === succeededToken : !hasError;
          const details: Array<[string, string]> = [
            ...sent,
            ["method called", "requestPermissionsAsync"],
            ["asyncResult.status", safeRead(() => asyncResult?.status)],
            ["asyncResult.value", safeRead(() => asyncResult?.value)],
            ["asyncResult.error.code", safeRead(() => asyncResult?.error?.code)],
            ["asyncResult.error.name", safeRead(() => asyncResult?.error?.name)],
            ["asyncResult.error.message", safeRead(() => asyncResult?.error?.message)],
          ];
          if (!succeeded) {
            finish({
              ...base,
              status: "fail",
              summary: `denied or errored — ${describeError(asyncResult?.error) || "no error object supplied"}`,
              details,
            });
            return;
          }
          finish({
            ...base,
            status: "pass",
            summary:
              asyncResult?.value === true
                ? "granted for the first time — the frame must RELOAD before the webview honours it"
                : "already granted (value=false per the .d.ts) — no reload needed",
            details,
          });
        });
      } catch (err) {
        clearTimeout(timer);
        finish({
          ...base,
          status: "fail",
          summary: `requestPermissionsAsync threw — ${describeError(err)}`,
          details: [...sent, ["method called", "requestPermissionsAsync"]],
        });
      }
    });
  }

  if (typeof target.requestPermissions === "function") {
    try {
      const granted = await target.requestPermissions([enumValue]);
      return {
        ...base,
        status: "pass",
        summary:
          granted === true
            ? "granted for the first time — the frame must RELOAD before the webview honours it"
            : "already granted (resolved false per the .d.ts) — no reload needed",
        details: [
          ...sent,
          ["method called", "requestPermissions (promise flavour)"],
          ["resolved value", String(granted)],
        ],
      };
    } catch (err) {
      return {
        ...base,
        status: "fail",
        summary: `requestPermissions rejected — ${describeError(err)}`,
        details: [...sent, ["method called", "requestPermissions (promise flavour)"]],
      };
    }
  }

  return {
    ...base,
    status: "unavailable",
    summary: "device-permission object exists but exposes neither request method",
    details: sent,
  };
}

/* -------------------------------------------------------------------------- */
/* Probe 5 — getUserMedia (operator-triggered)                                */
/* -------------------------------------------------------------------------- */

function pendingGetUserMediaRow(permissionState: string): ProbeRow {
  return {
    id: GET_USER_MEDIA_ROW_ID,
    label: "navigator.mediaDevices.getUserMedia({audio:true})",
    status: "unavailable",
    summary: 'not run yet — tap "Probe getUserMedia"',
    details: [["navigator.permissions.query({name:'microphone'})", permissionState]],
  };
}

/** Passive, non-prompting read of the browser's stored mic permission state. */
async function readMicPermissionState(): Promise<string> {
  try {
    const permissions = (
      navigator as unknown as {
        permissions?: { query?: (d: { name: string }) => Promise<{ state?: unknown }> };
      }
    ).permissions;
    if (!permissions || typeof permissions.query !== "function") {
      return "<Permissions API unavailable>";
    }
    const status = await permissions.query({ name: "microphone" });
    return safeRead(() => status?.state);
  } catch (err) {
    return `<threw ${describeError(err)}>`;
  }
}

/**
 * Open a real audio stream and close it immediately. Every track is stopped in
 * a `finally`, so the mic indicator flashes on and straight back off — the
 * probe never holds the device.
 */
export async function probeMicrophoneStream(): Promise<ProbeRow> {
  const base = {
    id: GET_USER_MEDIA_ROW_ID,
    label: "navigator.mediaDevices.getUserMedia({audio:true})",
  };
  const permissionState = await readMicPermissionState();
  const permissionDetail: [string, string] = [
    "navigator.permissions.query({name:'microphone'})",
    permissionState,
  ];

  const mediaDevices =
    typeof navigator !== "undefined"
      ? (navigator as Navigator & { mediaDevices?: MediaDevices }).mediaDevices
      : undefined;
  if (!mediaDevices || typeof mediaDevices.getUserMedia !== "function") {
    return {
      ...base,
      status: "unavailable",
      summary: "navigator.mediaDevices.getUserMedia does not exist in this webview",
      details: [permissionDetail],
    };
  }

  let stream: MediaStream | undefined;
  try {
    stream = await mediaDevices.getUserMedia({ audio: true });
    const labels = stream
      .getTracks()
      .map((t) => `${t.kind}:${t.label || "<no label>"}`)
      .join(", ");
    return {
      ...base,
      status: "pass",
      summary: `live audio stream acquired (${stream.getTracks().length} track(s)), released immediately`,
      details: [permissionDetail, ["tracks", labels || "<none>"]],
    };
  } catch (err) {
    const name =
      err && typeof err === "object" && "name" in err
        ? safeRead(() => (err as { name?: unknown }).name)
        : "<no name>";
    const message =
      err && typeof err === "object" && "message" in err
        ? safeRead(() => (err as { message?: unknown }).message)
        : "<no message>";
    return {
      ...base,
      status: "fail",
      summary: `${name}: ${message}`,
      details: [
        permissionDetail,
        ["DOMException.name", name],
        ["DOMException.message", message],
      ],
    };
  } finally {
    // Never hold the mic open, even if reading the tracks above threw.
    try {
      stream?.getTracks().forEach((t) => t.stop());
    } catch {
      /* nothing further we can do */
    }
  }
}

/* -------------------------------------------------------------------------- */
/* Probe 6 — nested iframe                                                    */
/* -------------------------------------------------------------------------- */

function probeFrameNesting(): ProbeRow {
  const base = { id: "nested-frame", label: "Frame nesting (top-level vs nested iframe)" };
  if (typeof window === "undefined") {
    return { ...base, status: "unavailable", summary: "no window object" };
  }

  let nested: boolean;
  try {
    nested = window.self !== window.top;
  } catch {
    // A cross-origin `window.top` comparison should not throw, but a hostile
    // webview could. Treat an unreadable top as nested — that is the risky case.
    nested = true;
  }

  let depth = 0;
  try {
    let cursor: Window = window;
    while (cursor !== cursor.parent && depth < 20) {
      cursor = cursor.parent;
      depth += 1;
    }
  } catch {
    depth = -1;
  }

  const frameElement = (() => {
    try {
      const el = window.frameElement;
      if (!el) return "<null — same-origin parent reports no frame element>";
      const allow = el.getAttribute("allow");
      return `<${el.tagName.toLowerCase()}> allow="${allow ?? "<not set>"}"`;
    } catch (err) {
      return `<unreadable (cross-origin): ${describeError(err)}>`;
    }
  })();

  return {
    ...base,
    status: nested ? "fail" : "pass",
    summary: nested
      ? `running in a NESTED frame (depth ${depth}) — a mic request from here is blocked; it must run on the top-level window`
      : "running on the top-level window (window.self === window.top)",
    details: [
      ["window.self === window.top", String(!nested)],
      ["frame depth above this window", depth < 0 ? "<unreadable>" : String(depth)],
      ["window.frameElement", frameElement],
    ],
  };
}

/* -------------------------------------------------------------------------- */
/* Probe 7 — RTCPeerConnection                                                */
/* -------------------------------------------------------------------------- */

function probePeerConnection(): ProbeRow {
  const base = { id: "rtc-peer-connection", label: "WebRTC (new RTCPeerConnection)" };
  const Ctor = (globalThis as { RTCPeerConnection?: new () => { close?: () => void } })
    .RTCPeerConnection;
  if (typeof Ctor !== "function") {
    return {
      ...base,
      status: "unavailable",
      summary: "RTCPeerConnection is not defined in this webview — Realtime cannot run here",
    };
  }
  let pc: { close?: () => void } | undefined;
  try {
    pc = new Ctor();
    return {
      ...base,
      status: "pass",
      summary: "constructed and closed cleanly",
    };
  } catch (err) {
    return {
      ...base,
      status: "fail",
      summary: `constructor threw — ${describeError(err)}`,
    };
  } finally {
    try {
      pc?.close?.();
    } catch {
      /* nothing further we can do */
    }
  }
}

/* -------------------------------------------------------------------------- */
/* Report assembly + text rendering                                           */
/* -------------------------------------------------------------------------- */

/**
 * Run every probe that does NOT raise a permission prompt, and seed the two
 * that do with "not run yet" placeholders. Never throws.
 */
export async function probeHostCapability(): Promise<HostCapabilityReport> {
  const micPermissionState = await readMicPermissionState();
  return {
    generatedAt: new Date().toISOString(),
    href: typeof window !== "undefined" ? window.location.href : null,
    userAgent: typeof navigator !== "undefined" ? navigator.userAgent : null,
    rows: [
      probeHostIdentity(),
      ...probeRequirementSets(),
      probeDevicePermissionApi(),
      pendingOfficePermissionRow(),
      pendingGetUserMediaRow(micPermissionState),
      probeFrameNesting(),
      probePeerConnection(),
    ],
  };
}

const STATUS_WIDTH = 11;

/**
 * Render the report as plain text. This is the deliverable — the operator runs
 * the panel on each host and pastes this back, so it must survive a copy into
 * any plain-text field with no formatting to lose.
 */
export function formatHostCapabilityReport(report: HostCapabilityReport): string {
  const lines: string[] = [
    "oraChat Outlook add-in - host capability diagnostics",
    "===================================================",
    `generated : ${report.generatedAt}`,
    `url       : ${report.href ?? "<unknown>"}`,
    `userAgent : ${report.userAgent ?? "<unknown>"}`,
    "",
  ];
  for (const row of report.rows) {
    lines.push(`[${row.status.toUpperCase().padEnd(STATUS_WIDTH)}] ${row.label} - ${row.summary}`);
    for (const [key, value] of row.details ?? []) {
      lines.push(`${" ".repeat(STATUS_WIDTH + 3)}${key} = ${value}`);
    }
  }
  lines.push(
    "",
    "Legend: PASS = capability confirmed on this host. FAIL = the probe ran and the",
    "capability was refused or absent. UNAVAILABLE = the probe could not run (API",
    "missing, or an operator-triggered probe that has not been tapped yet).",
  );
  return lines.join("\n");
}
